/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import OpenedSvg from '../../../static/icons/Opened';
import ClosedSvg from '../../../static/icons/Closed';
import config from '../../../config';
import styled from '@emotion/styled';
import { Link } from '../Link/Link';
import { Fragment } from 'react';
import { colors, effects } from '../../theme';

const ContentLink = styled(({ className, link, children }) =>
  link ? (
    <Link to={link} className={className}>
      {children}
    </Link>
  ) : (
    <a className={className}>{children}</a>
  )
)`
  color: ${(props) => props.theme.navigationSidebar.font.base};
  flex: 1;
  font-size: 14px;
  text-decoration: none;
  // font-weight: 500;
  line-height: 1.05;
  padding: 7px 21px 7px 19px;
`;

const NodeContent = styled(({ className, text, link, onClick, isCollapseSwitchButton, isExpanded }) => (
  <li onClick={onClick} className={className}>
    <ContentLink link={link}>{text}</ContentLink>
    {isCollapseSwitchButton && <NodeCollapseButton isExpanded={isExpanded} toggle={onClick} />}
  </li>
))`
  list-style: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  align-content: stretch;
  justify-content: space-between;
  cursor: pointer;
  letter-spacing: 0.05em;
  &:hover {
    text-shadow: ${effects.glow.textShadow};
    span {
      color: ${colors.font};
    }
    svg {
      border-radius: 20px;
      box-shadow: 0px 0px 14px -3px ${colors.primary};
    }
    svg path {
      fill: ${colors.font};
    }
  }
  > a,
  > button {
    transition: ${(props) => props.theme.transitions.hover};
  }
`;

const NestedContentTreeNode = styled(
  ({ className, location, children, toggle, expandedNavItems, setExpandedNavItems }) => (
    <ul className={className}>
      {children.map((item) => (
        <ContentTreeNode
          key={item.url}
          toggle={toggle}
          location={location}
          expandedNavItems={expandedNavItems}
          setExpandedNavItems={setExpandedNavItems}
          {...item}
        />
      ))}
    </ul>
  )
)`
  flex: 100%;
  li {
    margin-left: 17px;
    border-left: 1px solid ${(props) => props.theme.navigationSidebar.font.nested};
    a {
      color: ${(props) => props.theme.navigationSidebar.font.nested};
    }
  }
`;

const NodeCollapseButton = styled(({ className, isExpanded }) => {
  return (
    <span aria-label="collapse" className={className}>
      {isExpanded ? <OpenedSvg /> : <ClosedSvg />}
    </span>
  );
})`
  // background: transparent;
  border: none;
  border-radius: 20px;
  outline: none;
  z-index: 10;
  cursor: pointer;
  padding: 0 25px 0 10px !important;
  svg path {
    fill: ${(props) => props.theme.navigationSidebar.font.base};
  }
`;

const activeNodeCss = css({
  borderRight: 'none',
  fontWeight: 500,
  '> a, button': {
    color: `${colors.primary} !important`
  }
});

const ContentTreeNode = ({ className, url, title, location, children, expandedNavItems, setExpandedNavItems }) => {
  const hasChildren = children.length > 0;
  const active =
    location &&
    (location.pathname === url ||
      location.pathname === `${url}/` ||
      location.pathname === config.metadata.pathPrefix + url);
  const isExpanded = expandedNavItems[url];
  const toggle = () => {
    setExpandedNavItems({ ...expandedNavItems, [url]: !isExpanded });
  };

  return (
    <Fragment>
      <NodeContent
        text={title}
        link={hasChildren ? null : url}
        className={className}
        css={active ? activeNodeCss : ''}
        onClick={toggle}
        isCollapseSwitchButton={title && hasChildren}
        isExpanded={isExpanded}
      />
      {isExpanded && (
        <NestedContentTreeNode
          expandedNavItems={expandedNavItems}
          setExpandedNavItems={setExpandedNavItems}
          location={location}
        >
          {children}
        </NestedContentTreeNode>
      )}
    </Fragment>
  );
};
export default ContentTreeNode;
